import { lazy } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router";
import { AppShell } from "./app/AppShell";
import { BalanceProvider } from "./app/BalanceProvider";
import { OverlayProvider } from "./app/OverlayProvider";
import { RequireAnonymous, RequireStage, RequireVerifiable } from "./app/RequireStage";
import { RouteFocus } from "./app/RouteFocus";
import { SeatProvider } from "./app/SeatProvider";
import { SessionProvider } from "./app/SessionProvider";
import { TransportProvider } from "./app/TransportProvider";
import { AuthScreen } from "./routes/AuthScreen";
import { ForgotScreen } from "./routes/ForgotScreen";
import { NotFoundScreen } from "./routes/NotFoundScreen";
import { ResetScreen } from "./routes/ResetScreen";
import { SplashScreen } from "./routes/SplashScreen";
import { LegalScreen } from "./routes/LegalScreen";
import { VerifyScreen } from "./routes/VerifyScreen";
import { WelcomeScreen } from "./routes/WelcomeScreen";

/**
 * ── T-373 P1: the authenticated screens are lazy, the pre-login ones are not ──
 *
 * Every screen above is a **static** import because the splash/login/signup/
 * verify/welcome/legal path is what a first-time visitor paints, and a `Suspense`
 * fallback on first paint would be the flash-of-blank the ticket LOCKS against.
 *
 * The four screens below only exist behind `RequireStage stage="ready"` (they are
 * children of `AppShell`), so a visitor never reaches them before signing in —
 * and one of them (`LobbyScreen` → `useLobby`) is what drags the Colyseus SDK in.
 * Splitting them here is the same pattern the game engine already uses
 * (`game/registry.ts`'s `load: () => import(…)`): the code still renders exactly
 * what it did, it just loads when the route is reached instead of on first paint.
 * `AppShell` wraps its `<Outlet/>` in the `Suspense` boundary these need. The
 * Colyseus SDK is additionally pinned to its own chunk by `vite.config.ts`'s
 * `manualChunks`, so it can never creep back into the entry chunk.
 */
const LobbyScreen = lazy(() =>
  import("./routes/LobbyScreen").then((m) => ({ default: m.LobbyScreen })),
);
const RoomScreen = lazy(() =>
  import("./routes/RoomScreen").then((m) => ({ default: m.RoomScreen })),
);
const ProfileScreen = lazy(() =>
  import("./routes/ProfileScreen").then((m) => ({ default: m.ProfileScreen })),
);
const SettingsScreen = lazy(() =>
  import("./routes/StubScreens").then((m) => ({ default: m.SettingsScreen })),
);
/**
 * S8 E7 `SHOP-STOREFRONT-WEB` (T-404). No entry point points at `/shop`
 * while `shop_enabled` is false (Ivy §4.3 LOCKED) — it is reachable only by
 * typed URL or deep link, same as every other authenticated route here, so
 * it takes the same lazy treatment as its siblings.
 */
const ShopScreen = lazy(() =>
  import("./routes/ShopScreen").then((m) => ({ default: m.ShopScreen })),
);

/**
 * The Sprint 2 route table.
 *
 * The shape carries two of Ivy's LOCKED rules structurally rather than as
 * runtime conditions:
 *
 *  - **App chrome exists only inside `AppShell`.** The splash, every auth
 *    screen and `/welcome` are siblings of it, not children, so "no top bar on
 *    the identity gate" is not a flag anyone can forget to pass.
 *  - **Every guarded route names the stage of the routing matrix it belongs
 *    to.** The guard consults the one matrix; no screen invents a redirect.
 *
 * `BrowserRouter` (not a hash router) is why `infra/web/Caddyfile` needs the
 * SPA fallback: a hard refresh on `/welcome` is a request Caddy must answer
 * with `index.html`.
 */
/**
 * Module-level on purpose: the cache outlives every navigation, which is what
 * lets a player leave `/profile` and come back to the same expanded history
 * rather than to a fresh first page.
 *
 * **Exported for tests** (T-260). Because it *is* module-level, a jsdom file
 * that mounts `<App/>` twice shares one cache between the two mounts — so a
 * suite asserting a first render, a loading state or a failed query has to
 * `clear()` it between cases or it is asserting the previous test's data.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionProvider>
          {/*
            Above `OverlayProvider` on purpose: §14.1 r5 makes "is a seat held"
            an input to whether the conversion sheet may open at all, so the
            answer has to exist before the thing that opens it.
          */}
          {/*
            S4-8. Above `SeatProvider` because the chip is chrome and outlives
            every room: the number has to survive the socket that delivered it.
          */}
          <BalanceProvider>
            <SeatProvider>
              {/*
              Above the routes because the thing that *renders* the banner
              (`AppShell`) and the thing that *knows* the transport is down (the
              routed screen) are on opposite sides of an `<Outlet />`. S2 §13.2
              part 2 — the screen decides, the chrome draws.
            */}
              <TransportProvider>
                <OverlayProvider>
                  {/*
                §15 rule 11: an app-initiated route change moves focus to the
                destination's heading. Above `<Routes>` so it survives every
                screen swap and is impossible for a screen to forget.
              */}
                  <RouteFocus />
                  <Routes>
                    {/* The router-not-a-destination (§5). */}
                    <Route path="/" element={<SplashScreen />} />

                    {/* Auth — one screen, two routes, one initial tab each (§6). */}
                    <Route element={<RequireAnonymous />}>
                      <Route path="/login" element={<AuthScreen initialTab="login" />} />
                      <Route path="/signup" element={<AuthScreen initialTab="signup" />} />
                      <Route path="/forgot" element={<ForgotScreen />} />
                    </Route>

                    {/*
                  `/reset` is deliberately NOT behind `RequireAnonymous`: a
                  player can be signed in on one device and resetting on
                  another, and the token is the authority here, not the session.
                */}
                    <Route path="/reset" element={<ResetScreen />} />

                    {/*
                  Two arrivals, one with a session and one without — see
                  `RequireVerifiable`. A fresh sign-up has no session at all,
                  because verification is required before one exists.
                */}
                    <Route
                      path="/verify"
                      element={
                        <RequireVerifiable>
                          <VerifyScreen />
                        </RequireVerifiable>
                      }
                    />

                    {/* The mandatory identity gate — no chrome, by construction. */}
                    <Route
                      path="/welcome"
                      element={
                        <RequireStage stage="needs-identity">
                          <WelcomeScreen />
                        </RequireStage>
                      }
                    />

                    {/* Everything with app chrome. */}
                    <Route element={<RequireStage stage="ready" />}>
                      <Route element={<AppShell />}>
                        <Route path="/lobby" element={<LobbyScreen />} />
                        {/*
                        S4-7. **One route, one mounted component, for the entire
                        room lifecycle** (Ivy §2, LOCKED) — the phase transitions
                        never navigate. It is inside `AppShell` because S2 §10's
                        "absent on" list is exhaustive and does not include this
                        screen: the room keeps the top bar, the coin chip and the
                        connection banner in every state, including the round.
                      */}
                        <Route path="/room/:roomId" element={<RoomScreen />} />
                        <Route path="/settings" element={<SettingsScreen />} />
                        <Route path="/shop" element={<ShopScreen />} />
                        {/*
                          S6-2b. **`/profile` is the only identity-editing home
                          after the gate** (profile spec §2.1, LOCKED) — and it
                          replaces the panel's body, not the route table: the
                          route, its guard and its place inside `AppShell` are
                          unchanged. `/welcome` stays a gate (§2.2) and is still
                          `needs-identity`-guarded above.
                        */}
                        <Route path="/profile" element={<ProfileScreen />} />
                      </Route>
                    </Route>

                    {/*
                  The reviewed legal finals (T-234). Siblings of `AppShell`, not
                  children: they are reachable **pre-auth** from the attestation
                  line under the auth card, which is precisely when there is no
                  session and no chrome. Which document is a prop rather than a
                  `useLocation()` sniff inside the screen — the route table is
                  the one place that already knows.
                */}
                    <Route path="/terms" element={<LegalScreen doc="terms" />} />
                    <Route path="/privacy" element={<LegalScreen doc="privacy" />} />

                    {/*
                  `/dev/room` used to sit here, unlinked but reachable by URL.
                  §16f **A60** deleted it with the rest of the S1 dev surface, so
                  it is now an ordinary unknown path and falls through to
                  `NotFoundScreen` below — absent, not guarded.
                */}
                    <Route path="*" element={<NotFoundScreen />} />
                  </Routes>
                </OverlayProvider>
              </TransportProvider>
            </SeatProvider>
          </BalanceProvider>
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
